import React, { useEffect, useId, useRef } from 'react'
import cx from 'classnames'
import { useDrag } from 'react-dnd'
import { useFormContext, useWatch } from 'react-hook-form'
import { BtnV2, Card, CardButton, Checkbox, Icon, l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { getWaIdTooltipText } from 'apps/aiAgentEditor/components/ScenariosEditor/components/CollectInfoFieldSelector/components/WhatsAppIdCheckbox/lib/getWaIdTooltipText'
import { getAllFields } from 'common/fields/linking/accountAvailableFields'
import { useDndRef } from 'utils/commonHooks'

import { ConfigFormValues } from '../../../../lib/types'
import { ItemType } from '../DraggableField'

import cm from './styles.module.css'

interface FieldProps {
  index: number
  fieldId: string
  disabled: boolean
  onDelete: () => void
  onFieldClick: (index: number) => void
}

export const SelectedField = ({ fieldId, onDelete, index, onFieldClick, disabled }: FieldProps) => {
  const { setValue, control } = useFormContext<ConfigFormValues>()

  const useWaId = useWatch({
    name: 'useWaId',
    control,
  })

  const field = useAppSelector((state) => {
    const allFields = getAllFields(state)

    if (!allFields) {
      return null
    }

    return allFields[fieldId]
  })

  const handleDeleteClick = () => {
    if (fieldId === 'phone') {
      setValue('useWaId', false)
    }

    onDelete()
  }

  const checkboxId = useId()

  const cardRef = useRef<HTMLDivElement | null>(null)

  const [, drag, dragPreview] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  useEffect(() => {
    if (cardRef.current) {
      dragPreview(cardRef.current)
    }
  }, [dragPreview])

  const dragRef = useDndRef(drag)

  return (
    <div ref={cardRef}>
      <CardButton
        ariaLabel={l.translate('Change selected field')}
        onClick={() => onFieldClick(index)}
        className={cx(
          cm.selectedField,
          !field && cm.selectedField_deleted,
          disabled && cm.selectedField_disabled,
        )}
        disabled={disabled}
        buttonClassName={cm.selectedField__changeButton}
      >
        <div ref={dragRef} className={cx('leadQualFieldDragHandle', cm.selectedField__dragHandle)}>
          <Icon.DragItem size={16} />
        </div>

        <div className="text-ellipsis">
          <span className={cx('text-ellipsis', cm.selectedField__fieldName)}>
            {field?.caption || l.translate('Field deleted')}
          </span>

          {fieldId === 'phone' && (
            <Card.Action className="m-t-xs d-flex align-center p-b-xxs">
              <Checkbox
                checked={useWaId}
                onChange={(event) => {
                  setValue('useWaId', event.target.checked)
                }}
                id={checkboxId}
                disabled={disabled}
                ariaLabel={l.translate('Save phone as WhatsApp ID')}
              />

              <label
                htmlFor={checkboxId}
                className="d-flex align-center p-l-xs c-pointer gap-xxs font-regular"
              >
                <Icon.WhatsAppChannel size={16} />

                <span className="text-secondary">WhatsApp ID</span>

                {!disabled && <Icon.Question data-title={getWaIdTooltipText()} size={16} />}
              </label>
            </Card.Action>
          )}
        </div>

        <Card.Action>
          <BtnV2
            onClick={handleDeleteClick}
            className={cm.selectedField__deleteButton}
            ariaLabel={l.translate('Delete field')}
            variant="ghost-light"
            size="small"
            onlyIcon="DeleteForever"
          />
        </Card.Action>
      </CardButton>

      {!field && (
        <span className="d-block m-t-xxs p-l-xs text-body-sm text-danger">
          {l.translate('AI won’t ask it. You can select an another one.')}
        </span>
      )}
    </div>
  )
}
